import type { Variable } from "./types";
import type { SubanalysisSectionState } from "./subanalysis.types";

/** Ordinary input definitions and incomplete step references are not calls. */
export function isOrdinaryVariable(variable: {
  variable_role?: string | null;
}): boolean {
  return (
    variable.variable_role == null ||
    variable.variable_role === "ordinary" ||
    variable.variable_role === "step_binding"
  );
}

export function isJsonPointer(value: string): boolean {
  return value === "" || /^\/(?:[^~]|~[01])*$/.test(value);
}

const invalid = (message: string): SubanalysisSectionState => ({
  kind: "invalid",
  message,
});

const unsupported = (message: string): SubanalysisSectionState => ({
  kind: "unsupported",
  message,
});

/** Classify definitions without reading values or resolving runtime inputs. */
export function readSubanalysisSection(
  variables: Variable[],
): SubanalysisSectionState {
  const definitions = variables.filter((row) => !isOrdinaryVariable(row));
  if (!definitions.length) return { kind: "ordinary" };
  if (
    definitions.some(
      (row) =>
        !["subanalysis_call", "subanalysis_source", "bound_input"].includes(
          row.variable_role,
        ),
    )
  )
    return unsupported("This section uses an unsupported variable role.");

  const roots = definitions.filter(
    (row) => row.variable_role === "subanalysis_call",
  );
  const sources = definitions.filter(
    (row) => row.variable_role === "subanalysis_source",
  );
  if (roots.length !== 1 || sources.length !== 1)
    return invalid(
      "This section needs one child template and one earlier section selector.",
    );
  const root = roots[0]!;
  const source = sources[0]!;
  if (
    root.variable_kind !== "input" ||
    root.variable_type !== "uuid" ||
    root.variable_name !== "subanalysis_call" ||
    root.variable_group !== "subanalysis" ||
    !root.variable_is_required ||
    root.binding_kind !== "template" ||
    root.parent_variable_id !== null ||
    root.variable_default !== null ||
    root.analysis_id !== null ||
    !root.analysis_step_id ||
    root.binding_source_step_id !== null ||
    root.binding_source_variable_id !== null ||
    root.binding_target_variable_id !== null ||
    root.binding_path !== null
  )
    return invalid("The child template selector needs repair.");

  if (
    source.variable_kind !== "input" ||
    source.variable_type !== "uuid" ||
    source.variable_name !== "subanalysis_source" ||
    source.variable_group !== "subanalysis" ||
    !source.variable_is_required ||
    source.binding_kind !== "step_output" ||
    source.parent_variable_id !== root.variable_id ||
    source.analysis_id !== root.analysis_id ||
    source.analysis_step_id !== root.analysis_step_id ||
    source.tenant_id !== root.tenant_id ||
    source.record_tenant_id !== root.record_tenant_id ||
    source.binding_template_id !== null ||
    source.binding_target_variable_id !== null ||
    source.variable_default !== null ||
    typeof source.binding_path !== "string" ||
    !isJsonPointer(source.binding_path) ||
    (source.binding_source_step_id === null &&
      source.binding_source_variable_id !== null)
  )
    return invalid("The earlier section selector needs repair.");

  const inputs = definitions.filter(
    (row) => row.variable_role === "bound_input",
  );
  const targets = new Set<string>();
  for (const input of inputs) {
    if (
      input.variable_kind !== "input" ||
      input.variable_group !== "subanalysis" ||
      input.parent_variable_id !== root.variable_id ||
      !input.binding_target_variable_id ||
      input.analysis_id !== root.analysis_id ||
      input.analysis_step_id !== root.analysis_step_id ||
      input.tenant_id !== root.tenant_id ||
      input.record_tenant_id !== root.record_tenant_id ||
      input.binding_template_id !== null ||
      input.binding_source_step_id !== null ||
      targets.has(input.binding_target_variable_id)
    )
      return invalid(
        "A child input binding has a missing, duplicate, or invalid reference.",
      );
    targets.add(input.binding_target_variable_id);
    const noSource = input.binding_source_variable_id === null;
    const noPath = input.binding_path === null;
    const noDefault = input.variable_default === null;
    switch (input.binding_kind) {
      case "item":
        if (
          !noSource ||
          !noDefault ||
          typeof input.binding_path !== "string" ||
          !isJsonPointer(input.binding_path)
        )
          return invalid("An item binding needs a valid JSON Pointer.");
        break;
      case "parent_variable":
        if (!input.binding_source_variable_id || !noPath || !noDefault)
          return invalid("A parent variable binding needs a source variable.");
        break;
      case "step_variable":
        if (
          !input.binding_source_variable_id ||
          !noDefault ||
          (!noPath && !isJsonPointer(input.binding_path!))
        )
          return invalid(
            "A step variable binding needs a valid source and optional JSON Pointer.",
          );
        break;
      case "constant":
        if (!noSource || !noPath || input.variable_default === undefined)
          return invalid("A fixed value binding needs a configured value.");
        break;
      case "target_default":
      case "unbound":
        if (!noSource || !noPath || !noDefault)
          return invalid("A child input binding contains conflicting fields.");
        break;
      default:
        return unsupported("This section uses an unsupported binding mode.");
    }
  }
  return { kind: "subanalysis", root, source, inputs };
}
